import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import invoiceV2ApiService, {
  InvoiceV2Request,
  InvoiceV2Item,
  InvoiceV2PaymentInstruction,
} from "../../services/invoice-v2-api";
import userManagementApiService from "../../services/user-management-api";
import { Employee } from "../../types/leave";
import FileUpload from "../shared/FileUpload";
import SubmitFeedback from "../shared/SubmitFeedback";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { CURRENCIES, formatCurrencyDisplay } from "../../utils/currencyData";
import { createInvoiceFormFirstPageFromTemplate } from "../../utils/invoiceFormPdf";

interface InvoiceV2DialogProps {
  open: boolean;
  onClose: () => void;
  submissionType: "FILE_UPLOAD" | "CREATE_REQUEST";
  onSuccess: () => void;
  editingRequest?: InvoiceV2Request | null;
}

const InvoiceV2Dialog: React.FC<InvoiceV2DialogProps> = ({
  open,
  onClose,
  submissionType,
  onSuccess,
  editingRequest,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [employeeData, setEmployeeData] = useState<Employee | null>(null);

  // FILE_UPLOAD mode state
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [notes, setNotes] = useState<string>("");

  // CREATE_REQUEST mode state
  const [items, setItems] = useState<InvoiceV2Item[]>([]);
  const [currency, setCurrency] = useState<string>("JPY"); // Single currency for entire request
  const [totalAmount, setTotalAmount] = useState(0);
  const [optionalAttachmentFile, setOptionalAttachmentFile] =
    useState<File | null>(null);
  const [paymentInstruction, setPaymentInstruction] =
    useState<InvoiceV2PaymentInstruction>({
      swiftCode: "",
      accountName: "",
      accountCode: "",
      bankName: "",
      bankBranch: "",
      bankAddress: "",
      remarks: "",
    });

  // Load employee data and initialize/populate form when dialog opens
  useEffect(() => {
    if (open) {
      if (submissionType === "CREATE_REQUEST") {
        loadEmployeeData();
      }

      // Pre-populate form if editing
      if (editingRequest) {
        if (editingRequest.submissionType === "FILE_UPLOAD") {
          // FILE_UPLOAD mode - populate notes
          setNotes(editingRequest.notes || "");
          // Note: attachmentFile is optional during edit
        } else {
          // CREATE_REQUEST mode - populate items and payment instructions
          if (editingRequest.items && editingRequest.items.length > 0) {
            setItems(editingRequest.items);
            // Set currency from first item (all items have same currency)
            setCurrency(editingRequest.items[0].currency || "JPY");
          }
          if (editingRequest.paymentInstruction) {
            setPaymentInstruction(editingRequest.paymentInstruction);
          }
          setTotalAmount(editingRequest.totalAmount || 0);
          // Note: optionalAttachmentFile is optional during edit
        }
      } else {
        // Initialize with 1 empty item for new CREATE_REQUEST
        if (submissionType === "CREATE_REQUEST" && items.length === 0) {
          setItems([
            {
              description: "",
              quantity: 1,
              pricePerUnit: 0,
              currency: "JPY",
              amount: 0,
            },
          ]);
        }
      }
    }
  }, [open, submissionType, editingRequest]);

  const loadEmployeeData = async () => {
    try {
      const employee = await userManagementApiService.getMyProfile();
      setEmployeeData(employee);
    } catch (err) {
      console.error(t("error.failedToLoadEmployeeData"), err);
      // Continue without employee data if it fails
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetForm();
    }
  };

  const resetForm = () => {
    setAttachmentFile(null);
    setNotes("");
    setOptionalAttachmentFile(null);
    setItems([]);
    setCurrency("JPY");
    setTotalAmount(0);
    setPaymentInstruction({
      swiftCode: "",
      accountName: "",
      accountCode: "",
      bankName: "",
      bankBranch: "",
      bankAddress: "",
      remarks: "",
    });
    setError(undefined);
    setSuccess(undefined);
  };

  // Preset messages for FILE_UPLOAD notes
  const presetNotes = [
    t("invoice.v2.presetNote1"),
    t("invoice.v2.presetNote2"),
  ];

  // Handle preset note click
  const handlePresetNoteClick = (message: string) => {
    setNotes(message);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        pricePerUnit: 0,
        currency: currency, // Use request-level currency
        amount: 0,
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (
    index: number,
    field: keyof InvoiceV2Item,
    value: any,
  ) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Auto-calculate amount if quantity or price changes
    if (field === "quantity" || field === "pricePerUnit") {
      newItems[index].amount =
        (newItems[index].quantity || 0) * (newItems[index].pricePerUnit || 0);
    }

    setItems(newItems);

    // Recalculate total
    const newTotal = newItems.reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    );
    setTotalAmount(newTotal);
  };

  const handlePaymentInstructionChange = (
    field: keyof InvoiceV2PaymentInstruction,
    value: string,
  ) => {
    setPaymentInstruction({ ...paymentInstruction, [field]: value });
  };

  // Check if all items are valid
  const isValidItem = (item: InvoiceV2Item): boolean => {
    return (
      item.description.trim() !== "" &&
      item.quantity > 0 &&
      item.pricePerUnit > 0 &&
      item.currency !== ""
    );
  };

  const hasValidItems = (): boolean => {
    return items.length > 0 && items.every(isValidItem);
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      setError(t("error.requiredFieldsMissing"));
      return;
    }

    // Validate user information for CREATE_REQUEST
    if (submissionType === "CREATE_REQUEST") {
      if (
        !employeeData?.firstName ||
        !employeeData?.lastName ||
        !employeeData?.homeAddress
      ) {
        setError(t("profile.incompleteWarning"));
        return;
      }
    }

    if (submissionType === "FILE_UPLOAD") {
      // For FILE_UPLOAD: attachment is required for new submission, optional for edit
      if (!editingRequest && !attachmentFile) {
        setError(t("error.fileRequired"));
        return;
      }
      if (!notes.trim()) {
        setError(t("invoice.v2.notesRequired"));
        return;
      }
    } else {
      if (!hasValidItems()) {
        setError(t("invoice.v2.invalidItems"));
        return;
      }
    }

    setLoading(true);
    try {
      if (editingRequest) {
        // EDIT MODE
        if (submissionType === "FILE_UPLOAD") {
          // Edit FILE_UPLOAD request
          const request: InvoiceV2Request = {
            employeeId: user.id,
            submissionType: "FILE_UPLOAD",
            submissionDate: editingRequest.submissionDate,
            totalAmount: editingRequest.totalAmount,
            notes: notes.trim(),
          };

          await invoiceV2ApiService.editInvoiceRequest(
            editingRequest.requestId!,
            request,
            attachmentFile, // Optional - can be null to keep existing file
            null, // pdfFile (not used for FILE_UPLOAD)
            null, // optionalAttachmentFile (not used for FILE_UPLOAD)
            user.username || "",
            attachmentFile?.name,
            undefined, // pdfFileName (not used for FILE_UPLOAD)
          );
        } else {
          // Edit CREATE_REQUEST
          const itemsWithCurrency = items.map((item) => ({
            ...item,
            currency: currency,
          }));

          const request: InvoiceV2Request = {
            employeeId: user.id,
            submissionType: "CREATE_REQUEST",
            submissionDate: editingRequest.submissionDate,
            totalAmount,
            items: itemsWithCurrency,
            paymentInstruction,
          };

          // Generate PDF from invoice form
          let pdfFile: File | null = null;
          let pdfFileName: string;
          try {
            const pdfDoc = await createInvoiceFormFirstPageFromTemplate(
              request,
              {
                firstName: employeeData?.firstName || "",
                lastName: employeeData?.lastName || "",
                homeAddress: employeeData?.homeAddress || "",
              },
            );
            const pdfBytes = await pdfDoc.save();
            const pdfBlob = new Blob([new Uint8Array(pdfBytes)], {
              type: "application/pdf",
            });
            pdfFile = new File(
              [pdfBlob],
              `invoice-${request.submissionDate}.pdf`,
              { type: "application/pdf" },
            );
            pdfFileName = `invoice-${request.submissionDate}.pdf`;
          } catch (pdfErr) {
            console.error("Failed to generate PDF:", pdfErr);
            setError(t("error.failedToGeneratePDF"));
            setLoading(false);
            return;
          }

          // For CREATE_REQUEST edit: send PDF and optional attachment separately
          await invoiceV2ApiService.editInvoiceRequest(
            editingRequest.requestId!,
            request,
            null, // attachmentFile (not used for CREATE_REQUEST)
            pdfFile, // Always send the generated PDF
            optionalAttachmentFile, // Optional attachment (can be null)
            user.username || "",
            undefined, // mergedFileName (not used for CREATE_REQUEST)
            pdfFileName,
          );
        }
        setSuccess(t("success.requestUpdated"));
      } else {
        // CREATE MODE
        if (submissionType === "FILE_UPLOAD") {
          await invoiceV2ApiService.submitInvoiceFileUpload(
            user.id,
            attachmentFile!,
            user.username || "",
            attachmentFile!.name,
            notes.trim(),
          );
        } else {
          // CREATE_REQUEST mode - create new request
          // Apply request-level currency to all items before submission
          const itemsWithCurrency = items.map((item) => ({
            ...item,
            currency: currency,
          }));

          // Get local date (not UTC) for submission date
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, "0");
          const day = String(now.getDate()).padStart(2, "0");
          const submissionDate = `${year}-${month}-${day}`;

          const request: InvoiceV2Request = {
            employeeId: user.id,
            submissionType: "CREATE_REQUEST",
            submissionDate: submissionDate,
            totalAmount,
            items: itemsWithCurrency,
            paymentInstruction,
          };

          // Generate PDF from invoice form
          let pdfFile: File | null = null;
          let pdfFileName: string;
          try {
            const pdfDoc = await createInvoiceFormFirstPageFromTemplate(
              request,
              {
                firstName: employeeData?.firstName || "",
                lastName: employeeData?.lastName || "",
                homeAddress: employeeData?.homeAddress || "",
              },
            );
            const pdfBytes = await pdfDoc.save();
            const pdfBlob = new Blob([new Uint8Array(pdfBytes)], {
              type: "application/pdf",
            });
            pdfFile = new File(
              [pdfBlob],
              `invoice-${request.submissionDate}.pdf`,
              { type: "application/pdf" },
            );
            pdfFileName = `invoice-${request.submissionDate}.pdf`;
          } catch (pdfErr) {
            console.error("Failed to generate PDF:", pdfErr);
            setError(t("error.failedToGeneratePDF"));
            setLoading(false);
            return;
          }

          // Submit with generated PDF and optional attachment (stored separately)
          await invoiceV2ApiService.submitInvoiceCreateRequest(
            user.id,
            request,
            pdfFile,
            optionalAttachmentFile,
            user.username || "",
            pdfFileName,
          );
        }
        setSuccess(t("success.requestSubmitted"));
      }

      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToSubmitRequest"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Memoize items table rows to prevent expensive re-renders
  const itemsTableRows = useMemo(
    () =>
      items.map((item, index) => (
        <TableRow key={index}>
          <TableCell>
            <TextField
              size="small"
              value={item.description}
              onChange={(e) =>
                handleItemChange(index, "description", e.target.value)
              }
              fullWidth
            />
          </TableCell>
          <TableCell align="right">
            <TextField
              size="small"
              type="number"
              value={item.quantity}
              onChange={(e) =>
                handleItemChange(index, "quantity", parseFloat(e.target.value))
              }
              sx={{ width: 100 }}
            />
          </TableCell>
          <TableCell>
            <TextField
              size="small"
              type="number"
              value={item.pricePerUnit}
              onChange={(e) =>
                handleItemChange(
                  index,
                  "pricePerUnit",
                  parseFloat(e.target.value),
                )
              }
              sx={{ width: 100 }}
            />
          </TableCell>
          <TableCell align="center">{item.amount.toFixed(2)}</TableCell>
          <TableCell align="center">
            <IconButton
              size="small"
              onClick={() => handleRemoveItem(index)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      )),
    [items, currency],
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingRequest
            ? t("invoice.v2.editRequest")
            : submissionType === "FILE_UPLOAD"
              ? t("invoice.v2.uploadReceipt")
              : t("invoice.v2.newInvoice")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(undefined)}
            >
              {error}
            </Alert>
          )}

          {submissionType === "FILE_UPLOAD" ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t("invoice.v2.selectFile")}
              </Typography>

              {/* Show original attachment filename when editing */}
              {editingRequest && editingRequest.attachmentFilePath && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{t("invoice.v2.currentAttachment")}:</strong>{" "}
                    {editingRequest.attachmentFilePath}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {t("invoice.v2.attachmentEditInfo")}
                  </Typography>
                </Alert>
              )}

              <FileUpload
                onFileSelect={(files) => {
                  if (files && files.length > 0) {
                    setAttachmentFile(files[0]);
                  } else {
                    setAttachmentFile(null);
                  }
                }}
                selectedFiles={attachmentFile ? [attachmentFile] : []}
                maxFiles={1}
                acceptedFileTypes={[
                  ".pdf",
                  ".jpg",
                  ".jpeg",
                  ".png",
                  ".doc",
                  ".docx",
                ]}
              />

              {/* Notes field with preset messages */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("invoice.v2.notes")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>

                {/* Preset Messages */}
                <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {presetNotes.map((message, index) => (
                    <Chip
                      key={index}
                      label={message}
                      onClick={() => handlePresetNoteClick(message)}
                      variant="outlined"
                      size="small"
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(25, 118, 210, 0.08)",
                          borderColor: "primary.main",
                        },
                      }}
                    />
                  ))}
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("invoice.v2.notesPlaceholder")}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    {t("invoice.v2.items")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" color="secondary">
                      <strong>{t("invoice.v2.currency")}:</strong>
                    </Typography>
                    <Autocomplete
                      options={CURRENCIES}
                      getOptionLabel={(option) => formatCurrencyDisplay(option)}
                      value={
                        CURRENCIES.find((c) => c.code === currency) ||
                        CURRENCIES[0]
                      }
                      onChange={(_, newValue) => {
                        setCurrency(newValue?.code || "JPY");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ minWidth: 250 }}
                        />
                      )}
                      disableClearable
                    />
                  </Box>
                </Box>

                {items.length === 0 ? (
                  <Typography color="textSecondary" variant="body2">
                    {t("invoice.v2.noItems")}
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("invoice.v2.description")}</TableCell>
                        <TableCell align="center">
                          {t("invoice.v2.quantity")}
                        </TableCell>
                        <TableCell align="left">
                          {t("invoice.v2.price")}
                        </TableCell>
                        <TableCell align="center">
                          {t("invoice.v2.amount")}
                        </TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{itemsTableRows}</TableBody>
                  </Table>
                )}

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    sx={{ mt: 2, ml: 2, width: "60%", align: "center" }}
                  >
                    {t("common.add")}
                  </Button>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="subtitle2">
                      {t("invoice.v2.total")}:{" "}
                      {formatCurrencyDisplay(
                        CURRENCIES.find((c) => c.code === currency) ||
                          CURRENCIES[0],
                      )}{" "}
                      {totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("invoice.v2.paymentInstructions")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <TextField
                    label={t("invoice.v2.swiftCode")}
                    value={paymentInstruction.swiftCode}
                    onChange={(e) =>
                      handlePaymentInstructionChange(
                        "swiftCode",
                        e.target.value,
                      )
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("invoice.v2.accountName")}
                    value={paymentInstruction.accountName}
                    onChange={(e) =>
                      handlePaymentInstructionChange(
                        "accountName",
                        e.target.value,
                      )
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("invoice.v2.accountCode")}
                    value={paymentInstruction.accountCode}
                    onChange={(e) =>
                      handlePaymentInstructionChange(
                        "accountCode",
                        e.target.value,
                      )
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("invoice.v2.bankName")}
                    value={paymentInstruction.bankName}
                    onChange={(e) =>
                      handlePaymentInstructionChange("bankName", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("invoice.v2.bankBranch")}
                    value={paymentInstruction.bankBranch}
                    onChange={(e) =>
                      handlePaymentInstructionChange(
                        "bankBranch",
                        e.target.value,
                      )
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("invoice.v2.bankAddress")}
                    value={paymentInstruction.bankAddress}
                    onChange={(e) =>
                      handlePaymentInstructionChange(
                        "bankAddress",
                        e.target.value,
                      )
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("invoice.v2.remarks")}
                    value={paymentInstruction.remarks}
                    onChange={(e) =>
                      handlePaymentInstructionChange("remarks", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                </Box>
                {/* Optional Attachment for CREATE_REQUEST */}
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t("invoice.v2.optionalAttachment")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("invoice.v2.optionalAttachmentHelp")}
                  </Typography>

                  {/* Show original optional attachment filename when editing */}
                  {editingRequest && editingRequest.attachmentFilePath && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>
                          {t("invoice.v2.currentOptionalAttachment")}:
                        </strong>{" "}
                        {editingRequest.attachmentFilePath}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {t("invoice.v2.optionalAttachmentEditInfo")}
                      </Typography>
                    </Alert>
                  )}

                  <FileUpload
                    onFileSelect={(files) =>
                      setOptionalAttachmentFile(files?.[0] || null)
                    }
                    selectedFiles={
                      optionalAttachmentFile ? [optionalAttachmentFile] : []
                    }
                    acceptedFileTypes={[".pdf", ".jpg", ".jpeg", ".png"]}
                    maxFileSize={10 * 1024 * 1024}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              loading ||
              (submissionType === "CREATE_REQUEST" && !hasValidItems()) ||
              (submissionType === "FILE_UPLOAD" &&
                // For FILE_UPLOAD: attachment is required for new requests, optional for edits
                ((!editingRequest && !attachmentFile) || !notes.trim()))
            }
            title={
              submissionType === "CREATE_REQUEST" && !hasValidItems()
                ? t("invoice.v2.invalidItems")
                : submissionType === "FILE_UPLOAD" &&
                    !editingRequest &&
                    !attachmentFile
                  ? t("error.fileRequired")
                  : submissionType === "FILE_UPLOAD" && !notes.trim()
                    ? t("invoice.v2.notesRequired")
                    : ""
            }
          >
            {editingRequest ? t("common.update") : t("common.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitFeedback
        loading={loading}
        error={error}
        success={success}
        onCloseError={() => setError(undefined)}
        onCloseSuccess={() => setSuccess(undefined)}
      />
    </>
  );
};

export default InvoiceV2Dialog;
